import { useCallback, useContext } from 'react';
import { Contract } from 'ethers';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import ipfsClient from 'ipfs-http-client';
import {
  ENSNamespaceTypes,
  IAM,
  IRoleDefinition,
  setCacheClientOptions,
  VOLTA_CHAIN_ID,
  WalletProvider,
} from 'iam-client-lib';

import { IamContext } from 'api/context/iam/iamContext/IamContext';
import { ProxyMetadata } from 'hooks/useAssets/useAssets.types';
import { UserRole } from 'context/user/userContext/UserContext.types';

import { TokenData, useIamReturnType } from './useIam.types';

const getDataFromTokens = async (tokensUid: string[], contract: Contract) => {
  return (
    await Promise.all<ProxyMetadata>(
      tokensUid.map(async (tokenUid: string) => {
        const uri = await contract.uri(tokenUid);
        if (uri) {
          const response = await fetch(uri, { cache: 'no-store' });
          return response.json();
        } else {
          return {
            id: tokenUid,
          };
        }
      }),
    )
  ).filter(Boolean);
};

export const useIam: () => useIamReturnType = () => {
  const iamContext = useContext(IamContext);

  if (iamContext === undefined) {
    throw new Error('IamContext is unavailable, make sure you are using IamContextController');
  }

  const login = async (privateKey?: string) => {
    setCacheClientOptions(VOLTA_CHAIN_ID, {
      url: process.env.REACT_APP_CACHE_SERVER as string,
    });

    const iam = new IAM({
      privateKey,
      rpcUrl: process.env.REACT_APP_RPC_PROVIDER as string,
    });

    iamContext.setIam(iam);

    if (privateKey) {
      localStorage.setItem('ETH_PRIVATE_KEY', privateKey);
    }

    const { isMetamaskPresent } = await IAM.isMetamaskExtensionPresent();
    try {
      const result = await iam.initializeConnection({
        walletProvider: privateKey
          ? WalletProvider.PrivateKey
          : isMetamaskPresent
          ? WalletProvider.MetaMask
          : WalletProvider.WalletConnect,
        reinitializeMetamask: true,
      });
      return {
        connected: result.connected,
      };
    } catch {
      return {
        connected: false,
      };
    }
  };

  const logout = async () => {
    const { iam, setIam } = iamContext;

    localStorage.removeItem('ETH_PRIVATE_KEY');
    if (iam.current && iam.current.isConnected()) {
      await iam.current.closeConnection();
      setIam(null);
    }
    localStorage.removeItem('walletconnect');
  };

  const getClaims = async () => {
    const { iam } = iamContext;

    if (iam && iam.current?.isConnected()) {
      const claims = await iam.current.getUserClaims();

      const uniqueClaims: {
        [key: string]: Record<string, string | number | unknown>;
      } = {};
      for (const claim of claims || []) {
        if (!uniqueClaims[claim.id] && claim.id.indexOf('http') < 0) {
          uniqueClaims[claim.id] = claim;
        }
      }

      return Object.values(uniqueClaims) as ProxyMetadata[];
    }

    return [];
  };

  const createClaim = async (data: Record<string, unknown>) => {
    const { iam } = iamContext;

    if (iam.current && iam.current.isConnected()) {
      await iam.current.createSelfSignedClaim({ data });
    }
  };

  const getAllTokens = async () => {
    const { contract } = iamContext;
    if (contract) {
      const tokensUid = await contract.allTokens();
      return getDataFromTokens(tokensUid, contract);
    }
    return [];
  };

  const getCreatedTokensIds = async () => {
    const { contract, proxyContract } = iamContext;
    if (contract) {
      return contract.tokensCreatedBy(proxyContract.address);
    }
    return [];
  };

  const getOwnedTokensIds = async () => {
    const { iam, contract } = iamContext;

    const user = iam.current?.getSigner();
    if (contract && user) {
      return contract.tokensOwnedBy(await user.getAddress());
    }
    return [];
  };

  const getOwnedTokens = async () => {
    const { contract } = iamContext;
    const tokensUid = await getOwnedTokensIds();
    return getDataFromTokens(tokensUid, contract);
  };

  const getCreatedTokens = async () => {
    const { contract } = iamContext;
    const tokensUid = await getCreatedTokensIds();
    return getDataFromTokens(tokensUid, contract);
  };

  const saveToIpfs = async (token: Partial<ProxyMetadata>) => {
    const ipfs = ipfsClient(process.env.REACT_APP_IPFS_API_URL);
    const asyncChunks = await ipfs.add(JSON.stringify(token));
    let cid;
    for await (const chunk of asyncChunks) {
      cid = chunk.cid;
    }
    if (!cid) {
      throw new Error('IPFS error');
    }
    return `${process.env.REACT_APP_IPFS_URL}${cid.toString()}`;
  };

  const updateToken = async (token: Partial<ProxyMetadata>) => {
    const { iam, contract } = iamContext;

    const user = iam.current?.getSigner();
    if (user && contract) {
      const url = await saveToIpfs(token);
      const transaction = await contract.connect(user).updateUri(token.id, url);
      await transaction.wait();
      return true;
    }

    return false;
  };

  const addAgent = async (account: string) => {
    const { iam, contract } = iamContext;
    const user = iam.current?.getSigner();
    if (user && contract) {
      const transaction = await contract.connect(user).setApprovalForAll(account, true);
      await transaction.wait();
      return true;
    }
    return false;
  };

  const getAddress = async () => {
    const { iam } = iamContext;

    if (iam.current && iam.current.isConnected()) {
      const address = await iam.current.getSigner()?.getAddress();
      return address ?? '';
    }

    return '';
  };

  const mintTokens = async (tokens: string[]) => {
    const { proxyContract, iam } = iamContext;
    const user = iam.current?.getSigner();
    if (proxyContract && user) {
      for await (const token of tokens) {
        const transaction = await proxyContract.connect(user).create(token);
        await transaction.wait();
      }
      return true;
    }
    return false;
  };

  const transferTokens = async (tokens: string[], account: string) => {
    const { contract, iam } = iamContext;

    const user = iam.current?.getSigner();

    if (user && contract) {
      const userAddress = await user.getAddress();
      for await (const token of tokens) {
        const transaction = await contract.connect(user).transfer(userAddress, account, token, 1, '0x0');
        await transaction.wait();
      }

      return true;
    }

    return false;
  };

  const getUserData = async () => {
    const { iam } = iamContext;

    const address = await getAddress();
    const avatar = `https://blockies.shipchain.io/${address}.png?size=large`;

    const did = iam.current?.getDid() as string;
    const acceptedClaims = (await iam.current?.getClaimsByRequester({ did, isAccepted: true })) ?? [];

    if (acceptedClaims.length === 0) {
      return {
        role: null,
        name: '',
        avatar,
        address,
      };
    }

    const data = (await iam.current?.decodeJWTToken({
      token: acceptedClaims[0].token,
    })) as TokenData;

    const role = (await iam.current?.getDefinition({
      type: ENSNamespaceTypes.Roles,
      namespace: acceptedClaims[0].claimType,
    })) as IRoleDefinition;

    return {
      role: role.roleName as UserRole,
      name: data.claimData.fields.find(field => field.key === 'Name')?.value,
      avatar,
      address,
    };
  };

  return {
    isConnected: useCallback(() => !!iamContext.iam.current && iamContext.iam.current.isConnected(), [iamContext.iam]),
    login,
    logout,
    getClaims,
    createClaim,
    getAddress,
    getAllTokens,
    updateToken,
    addAgent,
    getOwnedTokens,
    getCreatedTokens,
    getCreatedTokensIds,
    getOwnedTokensIds,
    mintTokens,
    transferTokens,
    getUserData,
  };
};
